/**
 * Cliente HTTP do painel.
 *
 * Três decisões que valem a leitura:
 *
 * 1. **Nenhum token é guardado aqui.** A sessão vive em cookie `httpOnly`, que
 *    o navegador anexa sozinho e que este código (e qualquer outro JS da
 *    página) não consegue ler. Não há `localStorage`, então não há o que um XSS
 *    roubar.
 * 2. **CSRF por double-submit.** Como o cookie vai automático, todo método que
 *    escreve leva o header `x-csrf-token`, copiado do cookie `bx_csrf`. Um site
 *    de terceiros consegue disparar a requisição, mas não consegue LER nosso
 *    cookie para montar o header.
 * 3. **Renovação transparente.** Em 401, tenta UMA vez `POST /auth/refresh` e
 *    repete a chamada. Requisições concorrentes compartilham o mesmo refresh,
 *    senão cinco chamadas simultâneas rotacionariam o token cinco vezes e
 *    quatro delas seriam invalidadas pela própria rotação.
 */

const BASE = '/api';

/** Erro de API com o status HTTP preservado, para a UI reagir por código. */
export class ErroApi extends Error {
  constructor(
    readonly status: number,
    mensagem: string,
    readonly detalhes?: string[],
  ) {
    super(mensagem);
    this.name = 'ErroApi';
  }
}

function lerCookie(nome: string): string | undefined {
  return document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${nome}=`))
    ?.slice(nome.length + 1);
}

/** Chamado quando a sessão acaba de vez; a app usa para mandar ao login. */
let aoPerderSessao: () => void = () => {};
export function definirAoPerderSessao(fn: () => void): void {
  aoPerderSessao = fn;
}

/** Refresh em voo, compartilhado por todas as chamadas que tomaram 401. */
let refreshEmVoo: Promise<boolean> | null = null;

function renovarSessao(): Promise<boolean> {
  refreshEmVoo ??= fetch(`${BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
      'x-csrf-token': lerCookie('bx_csrf') ?? '',
    },
    body: '{}',
  })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      refreshEmVoo = null;
    });
  return refreshEmVoo;
}

interface Opcoes {
  metodo?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  corpo?: unknown;
  /** FormData para upload; ignora `corpo`. */
  arquivo?: FormData;
  /** Interno: evita laço infinito de renovação. */
  jaRenovou?: boolean;
}

async function bruto(caminho: string, opcoes: Opcoes = {}): Promise<Response> {
  const metodo = opcoes.metodo ?? 'GET';
  const headers: Record<string, string> = {};

  if (metodo !== 'GET') {
    headers['x-csrf-token'] = lerCookie('bx_csrf') ?? '';
  }
  if (opcoes.corpo !== undefined && !opcoes.arquivo) {
    headers['content-type'] = 'application/json';
  }

  const resposta = await fetch(`${BASE}${caminho}`, {
    method: metodo,
    // `same-origin`: painel e API compartilham a origem (o Nest serve os dois,
    // e em dev o Vite faz proxy), então o cookie viaja sem CORS.
    credentials: 'same-origin',
    headers,
    body: opcoes.arquivo ?? (opcoes.corpo !== undefined
      ? JSON.stringify(opcoes.corpo)
      : undefined),
  });

  if (resposta.status === 401 && !opcoes.jaRenovou) {
    // Access token de 15 min expirado é o caso comum aqui — renova e repete.
    if (await renovarSessao()) {
      return bruto(caminho, { ...opcoes, jaRenovou: true });
    }
    aoPerderSessao();
  }

  return resposta;
}

async function lancarSeErro(resposta: Response): Promise<void> {
  if (resposta.ok) return;

  let mensagem = `Falha na requisição (${resposta.status}).`;
  let detalhes: string[] | undefined;
  try {
    const corpo = (await resposta.json()) as {
      message?: string | string[];
    };
    if (Array.isArray(corpo.message)) {
      // O ValidationPipe devolve uma lista de mensagens por campo.
      detalhes = corpo.message;
      mensagem = corpo.message[0] ?? mensagem;
    } else if (typeof corpo.message === 'string') {
      mensagem = corpo.message;
    }
  } catch {
    // Resposta sem JSON (502 de gateway, HTML de erro) — fica a mensagem padrão.
  }
  throw new ErroApi(resposta.status, mensagem, detalhes);
}

/** Requisição que devolve JSON. Lança {@link ErroApi} em status de erro. */
export async function api<T>(caminho: string, opcoes: Opcoes = {}): Promise<T> {
  const resposta = await bruto(caminho, opcoes);
  await lancarSeErro(resposta);
  if (resposta.status === 204) return undefined as T;
  return (await resposta.json()) as T;
}

/** Como {@link api}, mas devolve `null` em 401 em vez de redirecionar. */
export async function apiOpcional<T>(caminho: string): Promise<T | null> {
  const resposta = await fetch(`${BASE}${caminho}`, {
    credentials: 'same-origin',
  });
  if (resposta.status === 401) return null;
  await lancarSeErro(resposta);
  return (await resposta.json()) as T;
}

/**
 * Baixa um arquivo autenticado.
 *
 * `fetch` + blob, e não `window.open`/`<a download>`: assim a requisição leva os
 * cookies pelo mesmo caminho das demais e um 404/401 vira erro tratado na tela,
 * em vez de abrir uma aba com JSON de erro.
 */
export async function baixarArquivo(
  caminho: string,
  nomeSugerido: string,
): Promise<void> {
  const resposta = await bruto(caminho);
  await lancarSeErro(resposta);

  const blob = await resposta.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeSugerido;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Sem isto o blob fica na memória da aba até o reload — um ZIP de lote pode
  // ter centenas de MB.
  URL.revokeObjectURL(url);
}
